import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import {
  Box,
  Typography,
  Chip,
  Button,
  IconButton,
  Drawer,
  Snackbar,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
} from "@mui/material";

import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import CloseIcon from "@mui/icons-material/Close";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import ListIcon from "@mui/icons-material/List";
import PersonIcon from "@mui/icons-material/Person";
import ContentCutIcon from "@mui/icons-material/ContentCut";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import EmailIcon from "@mui/icons-material/Email";
import PhoneIcon from "@mui/icons-material/Phone";
import NotesIcon from "@mui/icons-material/StickyNote2";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import EventBusyIcon from "@mui/icons-material/EventBusy";
import DoneAllIcon from "@mui/icons-material/DoneAll";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import PaymentsIcon from "@mui/icons-material/Payments";
import BlockIcon from "@mui/icons-material/Block";

import { useApi } from "../../hooks/useApi";
import { useDashContext } from "../../context/DashboardContext";
import { useTenant } from "../../hooks/useTenant";

import NewBookingDrawer from "./NewBookingDrawer";
import BlockDrawer from "./BlockDrawer";
import DesktopCalendarGrid from "./DesktopCalendarGrid";
import {
  BARBER_COLORS,
  STATUS_STYLES,
  REASON_LABELS,
} from "./calendarGridShared";

import { formatPhoneNumber } from "../../utils/functions";

dayjs.extend(utc);
dayjs.extend(timezone);

export default function BookingCalendar({ lockedBarber = null }) {
  const api = useApi();
  const { calendarDate, setCalendarDate, setBarberColors } = useDashContext();
  const { tenant } = useTenant();
  const tzName = tenant?.timezone || "America/New_York";

  // The calendar parses each booking's shop-local wall-clock string into a Date
  // using the BROWSER's timezone, so the Date's wall-clock components equal the
  // shop wall-clock the user sees. To send the correct UTC back to the API we
  // must re-interpret those wall-clock components in the SHOP timezone — NOT the
  // browser's. dayjs(date).utc() would (wrongly) treat the Date as a real instant
  // in the browser tz, shifting the time whenever the staff member's browser is
  // in a different timezone than the shop.
  const toShopUtc = useCallback(
    (date) =>
      dayjs
        .tz(dayjs(date).format("YYYY-MM-DD HH:mm:ss"), tzName)
        .utc()
        .format("YYYY-MM-DD HH:mm:ss"),
    [tzName],
  );

  const [barbers, setBarbers] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [weekCounts, setWeekCounts] = useState({});
  const [barberHours, setBarberHours] = useState({});
  const [dateOverrides, setDateOverrides] = useState({}); // { user_id: override | null } for current date
  const [selectedBarberIds, setSelectedBarberIds] = useState([]);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [newBookingPrefill, setNewBookingPrefill] = useState(null);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [toast, setToast] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [drawerMessage, setDrawerMessage] = useState(null);
  const [statusLoading, setStatusLoading] = useState(null);

  // Guards drag/resize against re-entrancy — a second move while the first PUT
  // is still in flight would race two optimistic updates against two refetches.
  const mutatingRef = useRef(false);

  // Block management
  const [blockEvents, setBlockEvents] = useState([]);
  const [blockDrawerOpen, setBlockDrawerOpen] = useState(false);
  const [blockDrawerPrefill, setBlockDrawerPrefill] = useState(null);
  const [selectedBlock, setSelectedBlock] = useState(null); // for delete dialog
  const [deletingBlock, setDeletingBlock] = useState(false);

  const visibleBarbers = useMemo(
    () =>
      selectedBarberIds.length === 0
        ? barbers
        : barbers.filter((b) => selectedBarberIds.includes(b.user_id)),
    [barbers, selectedBarberIds],
  );

  // ── Fetch barbers ────────────────────────────────────────────────────
  const fetchBarbers = useCallback(async () => {
    if (lockedBarber) {
      setBarbers([{ ...lockedBarber, color: BARBER_COLORS[0] }]);
      return;
    }
    try {
      const response = await api("/barbers/barbers.php", { method: "GET" });
      const list = (response.data.barbers || []).map((b, i) => ({
        ...b,
        color: BARBER_COLORS[i % BARBER_COLORS.length],
      }));
      setBarbers(list);
      // Publish the color map globally so other pages (e.g. overview) stay in sync
      const colorMap = {};
      list.forEach((b) => {
        colorMap[b.user_id] = b.color;
      });
      setBarberColors(colorMap);
    } catch (err) {
      showToast("Failed to load barbers", "error");
    }
  }, [lockedBarber]);

  // ── Fetch bookings ───────────────────────────────────────────────────
  const fetchBookings = useCallback(async () => {
    try {
      const dateStr = dayjs(currentDate).format("YYYY-MM-DD");
      const params = new URLSearchParams({
        date_from: dateStr,
        date_to: dateStr,
      });

      if (selectedBarberIds.length > 0) {
        params.set("barber_ids", selectedBarberIds.join(","));
      }

      const response = await api(`/tenants/appointments.php?${params}`, {
        method: "GET",
      });

      const mapped = (response.data.bookings || []).map((b) => ({
        id: b.id,
        status: b.status,
        starts_at: new Date(b.starts_at_local.replace(" ", "T")),
        ends_at: new Date(b.ends_at_local.replace(" ", "T")),
        price_charged: b.price_charged,
        payment_method: b.payment_method,
        payment_status: b.payment_status,
        amount_paid: b.amount_paid,
        notes: b.notes,
        client: b.client,
        barber: {
          ...b.barber,
          color:
            barbers.find((x) => String(x.user_id) === String(b.barber.user_id))
              ?.color || BARBER_COLORS[0],
        },
        service: b.service,
      }));

      setBookings(mapped);
      return mapped; // ✅ return so callers can refresh selectedBooking
    } catch (err) {
      showToast("Failed to load bookings", "error");
      return null;
    }
  }, [currentDate, selectedBarberIds, barbers]);

  // ── Fetch blocks for the current date (single batch call) ───────────
  const fetchBlocks = useCallback(async () => {
    if (!visibleBarbers.length) return;
    const dateStr = dayjs(currentDate).format("YYYY-MM-DD");
    const dayOfWeek = dayjs(currentDate).day();
    const dayStart = dayjs(currentDate).startOf("day").toDate();
    const dayEnd = dayjs(currentDate).endOf("day").toDate();

    try {
      const ids = visibleBarbers.map((b) => b.user_id).join(",");
      const res = await api(`/barbers/blocks.php?barber_ids=${ids}`, {
        method: "GET",
      });
      const blocksByBarber = res.data.blocks_by_barber || {};
      const allEvents = [];

      for (const barber of visibleBarbers) {
        const allBlocks = blocksByBarber[barber.user_id] || [];
        for (const block of allBlocks) {
          if (!block.is_recurring) {
            const bs = new Date(block.starts_at.replace(" ", "T") + "Z");
            const be = new Date(block.ends_at.replace(" ", "T") + "Z");
            if (bs < dayEnd && be > dayStart) {
              allEvents.push({
                id: `block-${block.id}`,
                starts_at: bs,
                ends_at: be,
                barber_user_id: barber.user_id,
                blockData: block,
              });
            }
          } else {
            const matchesDay =
              block.day_of_week === null || block.day_of_week === dayOfWeek;
            const afterStart =
              !block.recurring_start_date ||
              dateStr >= block.recurring_start_date;
            const beforeEnd =
              !block.recurring_end_date || dateStr <= block.recurring_end_date;
            if (matchesDay && afterStart && beforeEnd) {
              const [sh, sm] = block.start_time.split(":").map(Number);
              const [eh, em] = block.end_time.split(":").map(Number);
              const bs = new Date(currentDate);
              bs.setHours(sh, sm, 0, 0);
              const be = new Date(currentDate);
              be.setHours(eh, em, 0, 0);
              allEvents.push({
                id: `block-${block.id}-${dateStr}`,
                starts_at: bs,
                ends_at: be,
                barber_user_id: barber.user_id,
                blockData: block,
              });
            }
          }
        }
      }
      setBlockEvents(allEvents);
    } catch {
      // silently fail — blocks are non-critical display
    }
  }, [currentDate, visibleBarbers, api]);

  useEffect(() => {
    fetchBarbers();
  }, []);

  // Barber WEEKLY hours don't depend on the viewed date — fetch once per barber
  // set so clicking a new date doesn't refetch + re-render them needlessly.
  useEffect(() => {
    if (barbers.length > 0) fetchBarberHours(barbers);
  }, [barbers]);

  useEffect(() => {
    if (barbers.length > 0) {
      fetchBookings();
      fetchDateOverrides(barbers);
      fetchBlocks();
    }
  }, [currentDate, selectedBarberIds, barbers]);

  // Jump to a date requested from the overview page, then clear it
  useEffect(() => {
    if (!calendarDate) return;
    setCurrentDate(calendarDate);
    setCalendarDate(null);
  }, [calendarDate]);

  // Keep the open drawer in sync with the bookings array. Whenever bookings
  // is refreshed (e.g. after a status change), the drawer picks up the latest
  // data automatically without having to close and reopen.
  useEffect(() => {
    if (!selectedBooking) return;
    const updated = bookings.find((b) => b.id === selectedBooking.id);
    if (updated) setSelectedBooking(updated);
  }, [bookings]);

  // ── Fetch appointment counts for every day in the current week ───────
  const fetchWeekCounts = useCallback(async () => {
    const wStart = dayjs(currentDate).startOf("week").format("YYYY-MM-DD");
    const wEnd = dayjs(currentDate).endOf("week").format("YYYY-MM-DD");
    try {
      const res = await api(
        `/tenants/appointments.php?date_from=${wStart}&date_to=${wEnd}`,
        { method: "GET" },
      );
      const counts = {};
      (res.data.bookings || [])
        .filter((b) => b.status !== "cancelled")
        .forEach((b) => {
          const d = (b.starts_at_local || b.starts_at).slice(0, 10);
          counts[d] = (counts[d] || 0) + 1;
        });
      setWeekCounts(counts);
    } catch {
      // silently fail — strip shows "—" for days with no data
    }
  }, [currentDate, api]);

  // Re-fetch week counts only when the viewed week changes (not on every day click)
  const weekStartStr = dayjs(currentDate).startOf("week").format("YYYY-MM-DD");
  useEffect(() => {
    fetchWeekCounts();
  }, [weekStartStr]);

  // ── Fetch barber hours (single batch call) ───────────────────────────
  const fetchBarberHours = useCallback(async (barberList) => {
    if (!barberList.length) return;
    try {
      const ids = barberList.map((b) => b.user_id).join(",");
      const res = await api(`/barbers/schedule.php?barber_ids=${ids}`, {
        method: "GET",
      });
      const hoursByBarber = res.data.hours_by_barber || {};
      const map = {};
      barberList.forEach((b) => {
        map[b.user_id] = hoursByBarber[b.user_id] || [];
      });
      setBarberHours(map);
    } catch (err) {
      console.error("Failed to load barber hours", err);
    }
  }, []);

  // ── Fetch date overrides for the current day (single batch call) ─────
  const fetchDateOverrides = useCallback(
    async (barberList) => {
      if (!barberList.length) return;
      const dateStr = dayjs(currentDate).format("YYYY-MM-DD");
      try {
        const ids = barberList.map((b) => b.user_id).join(",");
        const res = await api(`/barbers/date-overrides.php?barber_ids=${ids}`, {
          method: "GET",
        });
        const overridesByBarber = res.data.overrides_by_barber || {};
        const map = {};
        barberList.forEach((b) => {
          const all = overridesByBarber[b.user_id] || [];
          map[b.user_id] = all.find((o) => o.date === dateStr) || null;
        });
        setDateOverrides(map);
      } catch {
        // silently fail
      }
    },
    [currentDate],
  );

  // ── Drag / resize commits from the grid ──────────────────────────────
  // The DesktopCalendarGrid computes the snapped/clamped target; these
  // handlers do the optimistic update + PUT + refetch (mirrors mobile web
  // and the RN app).
  const handleCommitMove = useCallback(
    async ({ booking, newStart, newEnd, targetBarber, barberChanged }) => {
      if (mutatingRef.current) return; // ignore drops while a move/resize is in flight

      // Optimistically update UI first
      setBookings((prev) =>
        prev.map((b) =>
          b.id === booking.id
            ? {
                ...b,
                starts_at: newStart,
                ends_at: newEnd,
                barber: targetBarber || b.barber,
              }
            : b,
        ),
      );

      mutatingRef.current = true;
      try {
        const body = {
          booking_id: booking.id,
          starts_at: toShopUtc(newStart),
          ends_at: toShopUtc(newEnd),
        };

        if (barberChanged) {
          body.barber_user_id = targetBarber.user_id;
        }

        const response = await api("/tenants/appointments.php", {
          method: "PUT",
          body: JSON.stringify(body),
        });

        if (response?.meta?.code === 200) {
          showToast(
            `Moved to ${dayjs(newStart).format("h:mm A")}${barberChanged ? ` · ${targetBarber.first_name}` : ""}`,
          );
          fetchBookings();
        }
      } catch (err) {
        // Show the specific API error (e.g. "This barber is not working on Monday")
        showToast(err?.message || "Failed to reschedule booking", "error");
        fetchBookings(); // revert optimistic update to server truth
      } finally {
        mutatingRef.current = false;
      }
    },
    [api, fetchBookings, toShopUtc],
  );

  const handleCommitResize = useCallback(
    async ({ booking, newEnd }) => {
      if (mutatingRef.current) return; // ignore resizes while a move/resize is in flight

      // Optimistic update
      setBookings((prev) =>
        prev.map((b) => (b.id === booking.id ? { ...b, ends_at: newEnd } : b)),
      );

      mutatingRef.current = true;
      try {
        const response = await api("/tenants/appointments.php", {
          method: "PUT",
          body: JSON.stringify({
            booking_id: booking.id,
            starts_at: toShopUtc(booking.starts_at),
            ends_at: toShopUtc(newEnd),
          }),
        });

        if (response?.meta?.code === 200) {
          const durationMin = Math.round((newEnd - booking.starts_at) / 60000);
          showToast(`Duration updated to ${durationMin} min`);
          fetchBookings();
        }
      } catch (err) {
        showToast(err?.message || "Failed to update duration", "error");
        fetchBookings(); // revert optimistic update to server truth
      } finally {
        mutatingRef.current = false;
      }
    },
    [api, fetchBookings, toShopUtc],
  );

  // Open the drawer immediately from data we already have. No refetch here —
  // bookings are loaded fresh on every date change, and the drawer-sync
  // effect keeps `selectedBooking` updated if `bookings` later changes.
  const handleSelectBooking = useCallback((booking) => {
    setSelectedBooking(booking);
  }, []);

  const handleSelectBlock = useCallback((blockData) => {
    setSelectedBlock(blockData);
  }, []);

  const handleDeleteBlock = async () => {
    if (!selectedBlock) return;
    setDeletingBlock(true);
    try {
      await api("/barbers/blocks.php", {
        method: "DELETE",
        body: JSON.stringify({ block_id: selectedBlock.id }),
      });
      setSelectedBlock(null);
      fetchBlocks();
      showToast("Block removed");
    } catch (err) {
      showToast(err?.message || "Failed to remove block", "error");
    } finally {
      setDeletingBlock(false);
    }
  };

  const handleSelectSlot = useCallback(
    ({ barber, time }) => {
      setNewBookingPrefill({
        barber,
        date: dayjs(time).format("YYYY-MM-DD"),
        slot: {
          start: dayjs(time).format("h:mm A"),
          start_utc: toShopUtc(time),
        },
      });
      setCreateDialogOpen(true);
    },
    [toShopUtc],
  );

  const handleStatusChange = async (newStatus) => {
    setStatusLoading(newStatus);
    setDrawerMessage(null);

    const bookingId = selectedBooking.id;

    try {
      if (newStatus === "cancelled") {
        await api("/stripe/cancel-booking.php", {
          method: "POST",
          body: JSON.stringify({ booking_id: bookingId }),
        });
        setDrawerMessage({
          message: "Booking cancelled — refund issued if paid by card.",
          severity: "success",
        });
      } else if (newStatus === "no_show") {
        await api("/stripe/charge-no-show.php", {
          method: "POST",
          body: JSON.stringify({ booking_id: bookingId }),
        });
        setDrawerMessage({
          message: "Booking marked as no-show.",
          severity: "success",
        });
      } else {
        await api("/tenants/appointments.php", {
          method: "PUT",
          body: JSON.stringify({ booking_id: bookingId, status: newStatus }),
        });
        setDrawerMessage({
          message: `Marked as ${STATUS_STYLES[newStatus]?.label.toLowerCase()}.`,
          severity: "success",
        });
      }

      await fetchBookings();
    } catch (err) {
      setDrawerMessage({
        message: err?.message || "Failed to update booking.",
        severity: "error",
      });
      await fetchBookings();
    } finally {
      setStatusLoading(null);
    }
  };

  const showToast = (message, severity = "success") =>
    setToast({ open: true, message, severity });

  const toggleBarber = (id) => {
    setSelectedBarberIds((prev) =>
      prev.length === 0
        ? [id]
        : prev.includes(id)
          ? prev.filter((x) => x !== id)
          : [...prev, id],
    );
  };

  const isAllSelected = selectedBarberIds.length === 0;

  const weekStart = dayjs(currentDate).startOf("week");
  const weekDays = Array.from({ length: 7 }, (_, i) => weekStart.add(i, "day"));

  // appointmentsByDay now comes from weekCounts (full-week fetch) so all 7 days show correct counts

  return (
    <Box sx={{ p: 4, width: "100%", mx: "auto" }}>
      {/* ── Header ───────────────────────────────────── */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 4,
        }}
      >
        <Box>
          <Typography variant="h3">Appointments</Typography>
          <Typography variant="body2" sx={{ color: "text.secondary" }}>
            Manage appointments and track status.
          </Typography>
        </Box>
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button
            variant="outlined"
            onClick={() => {
              setBlockDrawerPrefill({
                date: dayjs(currentDate).format("YYYY-MM-DD"),
              });
              setBlockDrawerOpen(true);
            }}
            sx={{
              textTransform: "none",
              fontWeight: 500,
              borderColor: "#e5e5e5",
              color: "#6b6b6b",
              "&:hover": { borderColor: "#1a1a1a", color: "#1a1a1a" },
            }}
          >
            Exceptions
          </Button>
          <Button
            variant="contained"
            color="primary"
            sx={{ backgroundColor: "#9c6a36" }}
            onClick={() => {
              setNewBookingPrefill(null);
              setCreateDialogOpen(true);
            }}
          >
            New booking
          </Button>
        </Box>
      </Box>

      {/* ── Barber pills ──────────────────────────────── */}
      {!lockedBarber && (
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
            gap: 1,
            mb: 4,
          }}
        >
          <Typography sx={{ fontSize: 13, color: "#6b6b6b" }}>
            Barbers
          </Typography>
          <Chip
            label="All"
            onClick={() => setSelectedBarberIds([])}
            sx={{
              backgroundColor: isAllSelected ? "#1a1a1a" : "white",
              color: isAllSelected ? "white" : "#1a1a1a",
              border: "1px solid",
              borderColor: isAllSelected ? "#1a1a1a" : "#e5e5e5",
              fontWeight: 500,
              fontSize: 13,
              height: 32,
              "&:hover": {
                backgroundColor: isAllSelected ? "#000" : "#f5f5f5",
              },
            }}
          />
          {barbers.map((barber) => {
            const sel =
              !isAllSelected && selectedBarberIds.includes(barber.user_id);
            return (
              <Chip
                key={barber.user_id}
                label={
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "row",
                      alignItems: "center",
                      gap: 1,
                    }}
                  >
                    <Box
                      sx={{
                        width: 7,
                        height: 7,
                        borderRadius: "50%",
                        backgroundColor: barber.color.dot,
                      }}
                    />
                    <span>
                      {barber.first_name} {barber.last_name}
                    </span>
                  </Box>
                }
                onClick={() => toggleBarber(barber.user_id)}
                sx={{
                  backgroundColor: sel ? barber.color.bg : "white",
                  border: `1px solid ${sel ? barber.color.border : "#e5e5e5"}`,
                  color: sel ? barber.color.text : "#1a1a1a",
                  fontWeight: 500,
                  fontSize: 13,
                  height: 32,
                  "& .MuiChip-label": { px: 1.25 },
                  "&:hover": {
                    backgroundColor: barber.color.bg,
                    borderColor: barber.color.border,
                  },
                }}
              />
            );
          })}
        </Box>
      )}

      {/* ── Date nav ─────────────────────────────────── */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
          gap: 1.5,
          mb: 2.5,
        }}
      >
        <Button
          onClick={() => setCurrentDate(new Date())}
          sx={{
            backgroundColor: "white",
            color: "#1a1a1a",
            textTransform: "none",
            fontWeight: 500,
            fontSize: 13,
            px: 2,
            py: 0.75,
            borderRadius: 1.5,
            border: "1px solid #e5e5e5",
            "&:hover": { backgroundColor: "#f5f5f5" },
          }}
        >
          Today
        </Button>
        <Box
          sx={{ display: "flex", flexDirection: "row", alignItems: "center" }}
        >
          <IconButton
            size="small"
            onClick={() =>
              setCurrentDate(dayjs(currentDate).subtract(7, "day").toDate())
            }
            sx={{ color: "#6b6b6b" }}
          >
            <ChevronLeftIcon fontSize="small" />
          </IconButton>
          <Typography
            sx={{
              fontSize: 14,
              fontWeight: 500,
              minWidth: 180,
              textAlign: "center",
            }}
          >
            {weekDays[0].format("MMM D")} – {weekDays[6].format("MMM D, YYYY")}
          </Typography>
          <IconButton
            size="small"
            onClick={() =>
              setCurrentDate(dayjs(currentDate).add(7, "day").toDate())
            }
            sx={{ color: "#6b6b6b" }}
          >
            <ChevronRightIcon fontSize="small" />
          </IconButton>
        </Box>
      </Box>

      {/* ── Week strip ────────────────────────────────── */}
      <Box sx={{ display: "flex", flexDirection: "row", gap: 1, mb: 4 }}>
        {weekDays.map((day) => {
          const isSel = day.isSame(currentDate, "day");
          const isToday = day.isSame(new Date(), "day");
          const count = weekCounts[day.format("YYYY-MM-DD")] || 0;
          return (
            <Box
              key={day.toString()}
              onClick={() => setCurrentDate(day.toDate())}
              sx={{
                flex: 1,
                py: 2,
                px: 1,
                borderRadius: 2,
                cursor: "pointer",
                textAlign: "center",
                backgroundColor: isSel ? "#1a1a1a" : "white",
                color: isSel ? "white" : "#1a1a1a",
                border: "1px solid",
                borderColor: isSel ? "#1a1a1a" : "#f0f0f0",
                transition: "all 0.15s",
                position: "relative",
                "&:hover": {
                  backgroundColor: isSel ? "#000" : "#fafafa",
                  borderColor: isSel ? "#000" : "#e0e0e0",
                },
              }}
            >
              {isToday && !isSel && (
                <Box
                  sx={{
                    position: "absolute",
                    top: 10,
                    right: 12,
                    width: 6,
                    height: 6,
                    borderRadius: "50%",
                    backgroundColor: "#9c6a36",
                  }}
                />
              )}
              <Typography
                sx={{
                  fontSize: 11,
                  color: isSel ? "white" : "#9b9b9b",
                  textTransform: "uppercase",
                  letterSpacing: 1.2,
                  fontWeight: 500,
                }}
              >
                {day.format("ddd")}
              </Typography>
              <Typography
                sx={{
                  fontSize: 26,
                  fontWeight: 600,
                  my: 0.25,
                  letterSpacing: -0.5,
                  color: isSel ? "white" : "inherit",
                }}
              >
                {day.format("D")}
              </Typography>
              <Typography
                sx={{
                  fontSize: 11,
                  color: isSel ? "white" : "#9b9b9b",
                  fontWeight: 500,
                }}
              >
                {count > 0 ? `${count} appt${count > 1 ? "s" : ""}` : "—"}
              </Typography>
            </Box>
          );
        })}
      </Box>

      {/* ── Legend ───────────────────────────────────── */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 1.5,
        }}
      >
        {!lockedBarber && (
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              flexWrap: "wrap",
              gap: 2,
            }}
          >
            {visibleBarbers.map((b) => (
              <Box
                key={b.user_id}
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "center",
                  gap: 0.75,
                }}
              >
                <Box
                  sx={{
                    width: 7,
                    height: 7,
                    borderRadius: "50%",
                    backgroundColor: b.color.dot,
                  }}
                />
                <Typography sx={{ fontSize: 12, color: "#6b6b6b" }}>
                  {b.first_name} {b.last_name}
                </Typography>
              </Box>
            ))}
          </Box>
        )}
        <Typography sx={{ fontSize: 12, color: "#9b9b9b" }}>
          Click an empty slot to book · Drag to reschedule · Drag the bottom
          edge to change duration
        </Typography>
      </Box>

      {/* ── Calendar ─────────────────────────────────── */}
      <DesktopCalendarGrid
        barbers={visibleBarbers}
        bookings={bookings}
        blockEvents={blockEvents}
        barberHours={barberHours}
        dateOverrides={dateOverrides}
        currentDate={currentDate}
        onSelectBooking={handleSelectBooking}
        onSelectBlock={handleSelectBlock}
        onSelectSlot={handleSelectSlot}
        onCommitMove={handleCommitMove}
        onCommitResize={handleCommitResize}
        onInvalidDrop={(msg) => showToast(msg, "error")}
      />

      {/* ── Detail drawer ─────────────────────────────── */}
      <Drawer
        anchor="right"
        open={!!selectedBooking}
        onClose={() => {
          setSelectedBooking(null);
          setDrawerMessage(null);
        }}
        slotProps={{
          paper: {
            sx: {
              width: { xs: "100%", sm: 300 },
            },
          },
        }}
      >
        {selectedBooking && (
          <Box
            sx={{ height: "100%", display: "flex", flexDirection: "column" }}
          >
            {/* Header */}
            <Box sx={{ p: 3, borderBottom: "1px solid #f0f0f0" }}>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "row",
                    alignItems: "baseline",
                    gap: 0.75,
                  }}
                >
                  <Typography sx={{ fontSize: 18, fontWeight: 700 }}>
                    Booking
                  </Typography>
                  <Typography sx={{ fontSize: 18, color: "#9b9b9b" }}>
                    #{selectedBooking.id}
                  </Typography>
                </Box>
                <IconButton
                  size="small"
                  onClick={() => {
                    setSelectedBooking(null);
                    setDrawerMessage(null);
                  }}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Box>
              <Chip
                label={STATUS_STYLES[selectedBooking.status]?.label}
                size="small"
                sx={{
                  mt: 1.5,
                  backgroundColor: STATUS_STYLES[selectedBooking.status]?.bg,
                  color: STATUS_STYLES[selectedBooking.status]?.text,
                  fontWeight: 600,
                  fontSize: 11,
                  height: 22,
                  letterSpacing: 0.5,
                }}
              />
            </Box>

            {/* Body */}
            <Box
              sx={{
                p: 3,
                flex: 1,
                overflowY: "auto",
              }}
            >
              {[
                {
                  icon: <PersonIcon sx={{ fontSize: 15, color: "#9b9b9b" }} />,
                  label: "Client",
                  content: (
                    <>
                      <Typography sx={{ fontSize: 15, fontWeight: 700 }}>
                        {selectedBooking.client.first_name}{" "}
                        {selectedBooking.client.last_name}
                      </Typography>
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          gap: 0.25,
                          mt: 0.5,
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "row",
                            alignItems: "center",
                            gap: 1,
                          }}
                        >
                          <EmailIcon sx={{ fontSize: 12, color: "#9b9b9b" }} />
                          <Typography
                            variant="caption"
                            sx={{ color: "text.secondary" }}
                          >
                            <a
                              href={`mailto:${selectedBooking.client.email}`}
                              style={{
                                color: "inherit",
                                textDecoration: "none",
                              }}
                            >
                              {selectedBooking.client.email}
                            </a>
                          </Typography>
                        </Box>
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "row",
                            alignItems: "center",
                            gap: 0.75,
                          }}
                        >
                          <PhoneIcon sx={{ fontSize: 12, color: "#9b9b9b" }} />

                          <Typography
                            variant="body2"
                            sx={{ color: "text.secondary" }}
                          >
                            <a
                              href={`tel:${selectedBooking.client.phone}`}
                              style={{
                                color: "inherit",
                                textDecoration: "none",
                              }}
                            >
                              {formatPhoneNumber(selectedBooking.client.phone)}
                            </a>
                          </Typography>
                        </Box>
                      </Box>
                    </>
                  ),
                },
                {
                  icon: (
                    <AccountCircleIcon
                      sx={{ fontSize: 15, color: "#9b9b9b" }}
                    />
                  ),
                  label: "Barber",
                  content: (
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "row",
                        alignItems: "center",
                        gap: 1,
                      }}
                    >
                      <Box
                        sx={{
                          width: 8,
                          height: 8,
                          borderRadius: "50%",
                          backgroundColor: selectedBooking.barber.color.dot,
                        }}
                      />
                      <Typography sx={{ fontSize: 15, fontWeight: 700 }}>
                        {selectedBooking.barber.first_name}{" "}
                        {selectedBooking.barber.last_name}
                      </Typography>
                    </Box>
                  ),
                },
                {
                  icon: (
                    <ContentCutIcon sx={{ fontSize: 15, color: "#9b9b9b" }} />
                  ),
                  label: "Service",
                  content: (
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "row",
                        alignItems: "center",
                        justifyContent: "space-between",
                      }}
                    >
                      <Typography sx={{ fontSize: 15, fontWeight: 700 }}>
                        {selectedBooking.service.name}
                      </Typography>
                      <Typography sx={{ fontSize: 15, fontWeight: 700 }}>
                        ${selectedBooking.price_charged.toFixed(2)}
                      </Typography>
                    </Box>
                  ),
                },
                {
                  icon: (
                    <PaymentsIcon sx={{ fontSize: 15, color: "#9b9b9b" }} />
                  ),
                  label: "Payment",
                  content: (() => {
                    const b = selectedBooking;
                    const pm = b.payment_method;
                    const ps = b.payment_status;
                    const paid = b.amount_paid;

                    // Resolve method label
                    const methodLabel =
                      (["cancelled", "no_show"].includes(b.status) &&
                        pm === "cash" &&
                        ps === "paid") ||
                      pm === "card"
                        ? "💳 Card"
                        : "💵 Cash";

                    // Resolve status label
                    let statusLabel = ps;
                    if (
                      b.status === "cancelled" &&
                      pm === "card" &&
                      ps === "refunded"
                    )
                      statusLabel = "Refunded";
                    else if (
                      b.status === "cancelled" &&
                      pm === "card" &&
                      ps === "paid"
                    )
                      statusLabel = "Non-refundable";
                    else if (
                      b.status === "cancelled" &&
                      pm === "cash" &&
                      ps === "unpaid"
                    )
                      statusLabel = "Cancelled";
                    else if (
                      b.status === "cancelled" &&
                      pm === "cash" &&
                      ps === "paid"
                    )
                      statusLabel = "Late cancellation fee";
                    else if (
                      b.status === "no_show" &&
                      pm === "card" &&
                      ps === "paid"
                    )
                      statusLabel = "Paid at booking";
                    else if (
                      b.status === "no_show" &&
                      pm === "cash" &&
                      ps === "paid"
                    )
                      statusLabel = "No-show fee charged";
                    else if (ps === "unpaid")
                      statusLabel = "Pay at appointment";
                    else if (ps === "paid") statusLabel = "Paid";

                    // Resolve amount
                    let amountLabel = `$${b.price_charged.toFixed(2)}`;
                    if (
                      ["cancelled", "no_show"].includes(b.status) &&
                      pm === "cash" &&
                      ps === "paid" &&
                      paid
                    ) {
                      amountLabel = `$${Number(paid).toFixed(2)} charged`;
                    } else if (pm === "card" && ps === "paid" && paid) {
                      amountLabel = `$${Number(paid).toFixed(2)}`;
                    }

                    return (
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          flexWrap: "wrap",
                        }}
                      >
                        <Typography
                          sx={{ fontSize: 14, fontWeight: 600, mr: 0.5 }}
                        >
                          {amountLabel}
                        </Typography>
                        <Box
                          sx={{
                            px: 1,
                            py: 0.25,
                            borderRadius: 1,
                            border: "1px solid #e5e5e5",
                            fontSize: 11,
                            fontWeight: 500,
                            color: "#4b4b4b",
                            backgroundColor: "#fafafa",
                          }}
                        >
                          {methodLabel}
                        </Box>
                        <Box
                          sx={{
                            px: 1,
                            py: 0.25,
                            borderRadius: 1,
                            fontSize: 11,
                            fontWeight: 500,
                            backgroundColor: [
                              "Non-refundable",
                              "Late cancellation fee",
                              "No-show fee charged",
                            ].includes(statusLabel)
                              ? "#FCEBEB"
                              : statusLabel === "Refunded"
                                ? "#E6F1FB"
                                : "#f0f0f0",
                            color: [
                              "Non-refundable",
                              "Late cancellation fee",
                              "No-show fee charged",
                            ].includes(statusLabel)
                              ? "#A32D2D"
                              : statusLabel === "Refunded"
                                ? "#0C447C"
                                : "#4b4b4b",
                          }}
                        >
                          {statusLabel}
                        </Box>
                      </Box>
                    );
                  })(),
                },
                {
                  icon: (
                    <AccessTimeIcon sx={{ fontSize: 15, color: "#9b9b9b" }} />
                  ),
                  label: "Time",
                  content: (
                    <>
                      <Typography sx={{ fontSize: 15, fontWeight: 700 }}>
                        {dayjs(selectedBooking.starts_at).format(
                          "dddd, MMMM D",
                        )}
                      </Typography>
                      <Typography
                        sx={{ fontSize: 13, color: "#6b6b6b", mt: 0.25 }}
                      >
                        {dayjs(selectedBooking.starts_at).format("h:mm A")} →{" "}
                        {dayjs(selectedBooking.ends_at).format("h:mm A")}
                        {" · "}
                        {Math.round(
                          (selectedBooking.ends_at -
                            selectedBooking.starts_at) /
                            60000,
                        )}{" "}
                        min
                      </Typography>
                    </>
                  ),
                },
              ].map(({ icon, label, content }, i) => (
                <Box key={label} sx={{ mb: 3 }}>
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "row",
                      alignItems: "center",
                      gap: 0.75,
                      mb: 1,
                    }}
                  >
                    {icon}
                    <Typography
                      sx={{
                        fontSize: 11,
                        color: "#9b9b9b",
                        textTransform: "uppercase",
                        letterSpacing: 0.8,
                        fontWeight: 600,
                      }}
                    >
                      {label}
                    </Typography>
                  </Box>
                  {content}
                </Box>
              ))}

              {selectedBooking.notes && (
                <Box sx={{ mb: 3 }}>
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "row",
                      alignItems: "center",
                      gap: 0.75,
                      mb: 1,
                    }}
                  >
                    <NotesIcon sx={{ fontSize: 15, color: "#9b9b9b" }} />
                    <Typography
                      sx={{
                        fontSize: 11,
                        color: "#9b9b9b",
                        textTransform: "uppercase",
                        letterSpacing: 0.8,
                        fontWeight: 600,
                      }}
                    >
                      Notes
                    </Typography>
                  </Box>
                  <Typography
                    sx={{
                      fontSize: 13,
                      color: "#4b4b4b",
                      fontStyle: "italic",
                      lineHeight: 1.6,
                    }}
                  >
                    {selectedBooking.notes}
                  </Typography>
                </Box>
              )}
            </Box>

            {/* Actions */}
            <Box
              sx={{
                p: 2.5,
                borderTop: "1px solid #f0f0f0",
                backgroundColor: "#fafafa",
              }}
            >
              {/* ── Inline status message ── */}
              {drawerMessage && (
                <Alert
                  severity={drawerMessage.severity}
                  onClose={() => setDrawerMessage(null)}
                  sx={{ mb: 1.5, fontSize: 12, borderRadius: 1.5 }}
                >
                  {drawerMessage.message}
                </Alert>
              )}
              {["completed", "cancelled", "no_show"].includes(
                selectedBooking.status,
              ) ? (
                <Typography
                  sx={{ fontSize: 12, color: "#9b9b9b", textAlign: "center" }}
                >
                  This booking is{" "}
                  {selectedBooking.status === "no_show"
                    ? "marked as no-show"
                    : selectedBooking.status}
                  . No further actions.
                </Typography>
              ) : (
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 1,
                  }}
                >
                  {selectedBooking.status === "pending" && (
                    <Button
                      startIcon={
                        statusLoading === "confirmed" ? (
                          <CircularProgress size={16} color="inherit" />
                        ) : (
                          <CheckCircleIcon sx={{ fontSize: 18 }} />
                        )
                      }
                      onClick={() => handleStatusChange("confirmed")}
                      disabled={!!statusLoading}
                      fullWidth
                      sx={{
                        backgroundColor: "#1a1a1a",
                        color: "white",
                        textTransform: "none",
                        fontWeight: 500,
                        py: 1,
                        borderRadius: 1.5,
                        "&:hover": { backgroundColor: "#000" },
                        "&.Mui-disabled": {
                          backgroundColor: "#1a1a1a",
                          color: "rgba(255,255,255,0.5)",
                        },
                      }}
                    >
                      Confirm Booking
                    </Button>
                  )}
                  <Box sx={{ display: "flex", flexDirection: "row", gap: 1 }}>
                    {[
                      {
                        label: "Complete",
                        icon: <DoneAllIcon sx={{ fontSize: 16 }} />,
                        status: "completed",
                        color: "#0F6E56",
                        hoverBg: "#f0f9f5",
                      },
                      {
                        label: "No-show",
                        icon: <EventBusyIcon sx={{ fontSize: 16 }} />,
                        status: "no_show",
                        color: "#854F0B",
                        hoverBg: "#fdf8ef",
                      },
                    ].map((btn) => (
                      <Button
                        key={btn.status}
                        startIcon={
                          statusLoading === btn.status ? (
                            <CircularProgress size={14} color="inherit" />
                          ) : (
                            btn.icon
                          )
                        }
                        onClick={() => handleStatusChange(btn.status)}
                        disabled={!!statusLoading}
                        sx={{
                          flex: 1,
                          backgroundColor: "white",
                          color: btn.color,
                          textTransform: "none",
                          fontWeight: 500,
                          fontSize: 13,
                          py: 0.75,
                          borderRadius: 1.5,
                          border: "1px solid #e5e5e5",
                          "&:hover": {
                            backgroundColor: btn.hoverBg,
                            borderColor: btn.color,
                          },
                        }}
                      >
                        {btn.label}
                      </Button>
                    ))}
                  </Box>
                  <Button
                    startIcon={
                      statusLoading === "cancelled" ? (
                        <CircularProgress size={14} color="inherit" />
                      ) : (
                        <CancelIcon sx={{ fontSize: 16 }} />
                      )
                    }
                    onClick={() => handleStatusChange("cancelled")}
                    disabled={!!statusLoading}
                    fullWidth
                    sx={{
                      backgroundColor: "white",
                      color: "#A32D2D",
                      textTransform: "none",
                      fontWeight: 500,
                      fontSize: 13,
                      py: 0.75,
                      borderRadius: 1.5,
                      border: "1px solid #e5e5e5",
                      "&:hover": {
                        backgroundColor: "#fdf6f6",
                        borderColor: "#A32D2D",
                      },
                    }}
                  >
                    Cancel Booking
                  </Button>
                </Box>
              )}
            </Box>
          </Box>
        )}
      </Drawer>

      {/* ── Create dialog ─────────────────────────────── */}
      {/* Only mounted while open. This drawer wraps Stripe <Elements> + a large
          form component; leaving it mounted full-time made it re-render on every
          BookingCalendar state change (every event click / dialog open), which is
          the main reason opening any drawer felt laggy. */}
      {createDialogOpen && (
        <NewBookingDrawer
          open={createDialogOpen}
          onClose={() => {
            setCreateDialogOpen(false);
            setNewBookingPrefill(null);
          }}
          barbers={barbers}
          preselectedBarber={newBookingPrefill?.barber || lockedBarber || null}
          preselectedDate={newBookingPrefill?.date || null}
          preselectedSlot={newBookingPrefill?.slot || null}
          onCreated={() => {
            fetchBookings();
            fetchWeekCounts();
            setCreateDialogOpen(false);
            setNewBookingPrefill(null);
          }}
        />
      )}

      {/* ── Block drawer ─────────────────────────────── */}
      {/* Same reasoning — only mount while open so it doesn't re-render with the
          calendar on unrelated state changes. */}
      {blockDrawerOpen && (
        <BlockDrawer
          open={blockDrawerOpen}
          onClose={() => setBlockDrawerOpen(false)}
          barbers={barbers}
          preselectedBarber={blockDrawerPrefill?.barber || null}
          preselectedDate={blockDrawerPrefill?.date || null}
          preselectedStartTime={blockDrawerPrefill?.startTime || null}
          onCreated={() => {
            setBlockDrawerOpen(false);
            fetchBlocks();
            fetchDateOverrides(barbers);
          }}
        />
      )}

      {/* ── Block delete dialog ───────────────────────── */}
      <Dialog
        open={!!selectedBlock}
        onClose={() => !deletingBlock && setSelectedBlock(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Remove block</DialogTitle>
        <DialogContent>
          {selectedBlock?.is_recurring && (
            <Alert severity="warning" sx={{ mb: 1.5 }}>
              This is a recurring block. Removing it will delete all future
              occurrences.
            </Alert>
          )}
          <Typography variant="body2" sx={{ color: "text.secondary" }}>
            Remove the{" "}
            <strong>{REASON_LABELS[selectedBlock?.reason] || "block"}</strong>{" "}
            from the calendar?
            {selectedBlock?.notes ? ` (${selectedBlock.notes})` : ""}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setSelectedBlock(null)}
            disabled={deletingBlock}
          >
            Keep
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDeleteBlock}
            disabled={deletingBlock}
          >
            {deletingBlock ? (
              <CircularProgress size={16} color="inherit" />
            ) : (
              "Remove"
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Toast ────────────────────────────────────── */}
      <Snackbar
        open={toast.open}
        autoHideDuration={3000}
        onClose={() => setToast((t) => ({ ...t, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity={toast.severity}
          variant="filled"
          onClose={() => setToast((t) => ({ ...t, open: false }))}
          sx={{ borderRadius: 2, fontWeight: 500 }}
        >
          {toast.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
